import { useState } from 'react';
import { Button } from 'antd';
import { useDispatch } from 'react-redux';
import { LuUpload } from 'react-icons/lu';
import { EmptyState } from '@/shared/ui/EmptyState';
import {
  UploadAPI,
  useGetUploadsSummaryQuery,
  useListUploadsQuery,
} from '@/state/slices/uploads';
import { usePageLoading } from '@/hooks/usePageLoading';
import UploadsSummaryCards from '@/features/uploads/UploadsSummaryCards';
import UploadsFilterBar from '@/features/uploads/UploadsFilterBar';
import UploadsTable from '@/features/uploads/UploadsTable';
import UploadStudyModal from '@/features/uploads/UploadStudyModal';
import { useUploadsListFilters } from '@/features/uploads/hooks/useUploadsListFilters';
import { uploadsPollingInterval } from '@/features/uploads/utils/pollingInterval';
import styles from './UploadsPage.module.css';

export default function UploadsPage() {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    params,
    page,
    size,
    searchText,
    setSearchText,
    selectedStatuses,
    onStatusesChange,
    selectedStudyTypes,
    onStudyTypesChange,
    selectedSites,
    onSitesChange,
    datePreset,
    onDatePresetChange,
    hasActiveFilters,
    hasActiveSearch,
    clearFilters,
    clearSearch,
    onTableChange,
  } = useUploadsListFilters();

  // Both queries share one interval, derived from what is already cached
  // (R8). Reading the cache first avoids mirroring it into state.
  const { data: cachedList } =
    UploadAPI.endpoints.listUploads.useQueryState(params);
  const { data: cachedSummary } =
    UploadAPI.endpoints.getUploadsSummary.useQueryState();
  const pollingInterval = uploadsPollingInterval(
    cachedList?.results,
    cachedSummary
  );
  const { data, currentData, isFetching, isLoading, isError, refetch } =
    useListUploadsQuery(params, { pollingInterval });
  const { data: summary, isLoading: summaryLoading } =
    useGetUploadsSummaryQuery(undefined, { pollingInterval });

  usePageLoading(isLoading);

  // Status is filtered by the server (R11): render the page as returned.
  const rows = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const hasData = rows.length > 0;
  // A poll or refetch keeps `currentData`, so it refreshes silently; only a
  // page or filter change, with nothing yet for the new query, spins (R16).
  const tableLoading = isFetching && !currentData;

  // The new row comes from the server (ai-pacs-be creates it at
  // uploads/init/), so a started or finished upload only refetches. Started:
  // the dialog has closed and the row shows `Uploading · x/N` from here on.
  const refreshUploads = () => {
    dispatch(
      UploadAPI.util.invalidateTags([
        { type: 'Uploads', id: 'LIST' },
        { type: 'Uploads', id: 'SUMMARY' },
      ])
    );
  };

  // Only a failure with nothing to show replaces the table. A failed refetch
  // keeps the last rows on screen (R13), since `data` outlives the error.
  const firstLoadFailed = isError && !data;

  const renderEmptyState = () => {
    if (firstLoadFailed) {
      return (
        <EmptyState
          headline="Couldn't load uploads"
          subtext="Something went wrong while loading the list."
          action={{ label: 'Retry', onClick: refetch }}
        />
      );
    }
    if (hasActiveSearch) {
      return (
        <EmptyState
          headline="No matching uploads"
          subtext="Try a different search term or clear your filters."
          action={{ label: 'Clear search', onClick: clearSearch }}
        />
      );
    }
    if (hasActiveFilters) {
      return (
        <EmptyState
          headline="No matching uploads"
          subtext="Try clearing or adjusting your filters."
          action={{ label: 'Reset filters', onClick: clearFilters }}
        />
      );
    }
    return (
      <EmptyState
        headline="No uploaded studies yet"
        subtext="Studies will appear here once they're ingested."
      />
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.topActions}>
        <Button
          type="primary"
          icon={<LuUpload size={14} />}
          onClick={() => setIsModalOpen(true)}
        >
          Upload study
        </Button>
      </div>

      <UploadsSummaryCards summary={summary} loading={summaryLoading} />

      <header className={styles.tableHeader}>
        <h2 className={styles.title}>Uploaded studies</h2>
        <span className={styles.counter}>
          {totalCount} {totalCount === 1 ? 'study' : 'studies'}
        </span>
      </header>

      <UploadsFilterBar
        searchText={searchText}
        onSearchChange={setSearchText}
        selectedStatuses={selectedStatuses}
        onStatusesChange={onStatusesChange}
        selectedStudyTypes={selectedStudyTypes}
        onStudyTypesChange={onStudyTypesChange}
        selectedSites={selectedSites}
        onSitesChange={onSitesChange}
        datePreset={datePreset}
        onDatePresetChange={onDatePresetChange}
      />

      {hasData ? (
        <UploadsTable
          data={data}
          loading={tableLoading}
          page={page}
          pageSize={size}
          onTableChange={onTableChange}
        />
      ) : (
        !isFetching && renderEmptyState()
      )}

      <UploadStudyModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUploadStarted={refreshUploads}
        onUploadSuccess={refreshUploads}
      />
    </div>
  );
}
